import { Controller } from "@hotwired/stimulus"

// Indies Cycle Summary Report (card #2345, WIN Article #183). The server
// ships every Independents cycle's (and its weekly children's) hard-saved/
// live ReportingSnapshot data as one JSON payload (see #connect) -- from
// there this controller owns ALL interactive state (view mode, tab
// selection, Layout Results sub-view, column sorting) with zero further
// round-trips. Mirrors client_layout_scores_mso_controller.js's
// "ship once, render client-side via template strings" architecture. No
// interactive filter bar (per the brief) -- the only interactions are the
// By Cycle/By Week toggle, tab selection, the Layout Results sub-view tabs,
// and column sorting within By Store/By Rep.
export default class extends Controller {
  static targets = ["viewCycleBtn", "viewWeekBtn", "tabBar", "content", "generatedNote", "payload"]
  static values = { generatedNote: String }

  connect() {
    this.reportData = this.readPayload()
    this.state = {
      viewMode: "cycles",
      activeId: null,
      layoutSubView: "summary",
      sortKey: { store: "rank", rep: "layouts" },
      sortDir: { store: "asc", rep: "desc" }
    }

    if (this.hasGeneratedNoteTarget) this.generatedNoteTarget.textContent = this.generatedNoteValue || ""

    this.setDefaultActiveId()
    this.render()
  }

  readPayload() {
    if (!this.hasPayloadTarget) return { cycles: [], weeks: [] }
    const raw = this.payloadTarget.textContent
    if (!raw || !raw.trim()) return { cycles: [], weeks: [] }
    return JSON.parse(raw)
  }

  currentCollection() {
    return this.state.viewMode === "cycles" ? (this.reportData.cycles || []) : (this.reportData.weeks || [])
  }

  // Defaults to the last entry (newest) in whichever collection is active --
  // mirrors AURORA always activating the newest cycle tab on load.
  setDefaultActiveId() {
    const collection = this.currentCollection()
    this.state.activeId = collection.length ? collection[collection.length - 1].id : null
  }

  // ---------- view mode toggle ----------

  showCycles() {
    if (this.state.viewMode === "cycles") return
    this.state.viewMode = "cycles"
    this.setDefaultActiveId()
    this.render()
  }

  showWeeks() {
    if (this.state.viewMode === "weeks") return
    this.state.viewMode = "weeks"
    this.setDefaultActiveId()
    this.render()
  }

  selectTab(event) {
    this.state.activeId = event.currentTarget.dataset.tabId
    this.render()
  }

  selectLayoutSubView(event) {
    this.state.layoutSubView = event.currentTarget.dataset.subView
    this.render()
  }

  setSort(event) {
    const view = event.currentTarget.dataset.sortView
    const key = event.currentTarget.dataset.sortKey

    if (this.state.sortKey[view] === key) {
      this.state.sortDir[view] = this.state.sortDir[view] === "asc" ? "desc" : "asc"
    } else {
      this.state.sortKey[view] = key
      this.state.sortDir[view] = "asc"
    }
    this.render()
  }

  // ---------- rendering ----------

  render() {
    this.renderViewToggle()
    this.renderTabBar()
    this.renderContent()
  }

  renderViewToggle() {
    if (this.hasViewCycleBtnTarget) this.viewCycleBtnTarget.classList.toggle("active", this.state.viewMode === "cycles")
    if (this.hasViewWeekBtnTarget) this.viewWeekBtnTarget.classList.toggle("active", this.state.viewMode === "weeks")
  }

  renderTabBar() {
    if (!this.hasTabBarTarget) return
    const collection = this.currentCollection()

    this.tabBarTarget.innerHTML = collection.map(entry => `
      <button type="button" class="ics-tab-btn ${entry.id === this.state.activeId ? "active" : ""}" data-tab-id="${entry.id}">
        ${entry.label}
      </button>
    `).join("")

    this.tabBarTarget.querySelectorAll("[data-tab-id]").forEach(btn => {
      btn.addEventListener("click", (event) => this.selectTab(event))
    })
  }

  activeEntry() {
    return this.currentCollection().find(entry => entry.id === this.state.activeId)
  }

  renderContent() {
    if (!this.hasContentTarget) return
    const entry = this.activeEntry()

    if (!entry) {
      this.contentTarget.innerHTML = `<div class="ics-placeholder-card">No data available yet.</div>`
      return
    }

    this.contentTarget.innerHTML = [
      this.renderKpiCards(entry),
      this.renderBreakdownTables(entry),
      this.renderLeaderboard(entry),
      this.renderDisplayTables(entry),
      this.renderLayoutSection(entry)
    ].join("")

    this.bindLayoutSubViewTabs()
    this.bindSortableHeaders()
  }

  renderKpiCards(entry) {
    const layoutSummary = entry.layouts && entry.layouts.tracked
      ? `${entry.layouts.score_rows.length} scored`
      : "not tracked"

    const card = (label, value, sub = "") => `
      <div class="ics-kpi-card">
        <div class="ics-kpi-label">${label}</div>
        <div class="ics-kpi-value">${value}</div>
        ${sub ? `<div class="ics-kpi-sub">${sub}</div>` : ""}
      </div>
    `

    return `
      <div class="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        ${card("Total Cut-Ins", entry.total_cut_ins ?? "–", `${entry.field_cut_ins ?? 0} field · ${entry.tio_cut_ins ?? 0} TIO`)}
        ${card("Display Builds", entry.display_builds ?? "–")}
        ${card("States Covered", (entry.by_state || []).length)}
        ${card("Clients", (entry.by_client || []).length)}
        ${card("Layouts", layoutSummary)}
      </div>
      ${entry.to_date ? `<div class="text-xs text-amber-600 font-medium mb-3">This period is still in progress -- figures are to date, generated ${entry.generated || ""}.</div>` : ""}
    `
  }

  renderBreakdownTables(entry) {
    const stateRows = (entry.by_state || []).map(r => `
      <tr class="odd:bg-white even:bg-gray-50">
        <td class="table-row-primary text-start">${r.state}</td>
        <td class="table-row-primary text-right">${r.cut_ins}</td>
      </tr>
    `).join("")

    const clientRows = (entry.by_client || []).map(r => `
      <tr class="odd:bg-white even:bg-gray-50">
        <td class="table-row-primary text-start">${r.client}</td>
        <td class="table-row-primary text-right">${r.cut_ins}</td>
      </tr>
    `).join("")

    return `
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div class="overflow-x-auto">
          <h3 class="text-sm font-semibold text-gray-700 mb-2">Cut-Ins by State</h3>
          <table class="table-primary">
            <thead><tr><th class="table-header-primary">STATE</th><th class="table-header-primary text-right">CUT-INS</th></tr></thead>
            <tbody class="table-body-primary">${stateRows || `<tr><td colspan="2" class="table-row-primary text-center text-gray-400">No data</td></tr>`}</tbody>
          </table>
        </div>
        <div class="overflow-x-auto">
          <h3 class="text-sm font-semibold text-gray-700 mb-2">Cut-Ins by Client</h3>
          <table class="table-primary">
            <thead><tr><th class="table-header-primary">CLIENT</th><th class="table-header-primary text-right">CUT-INS</th></tr></thead>
            <tbody class="table-body-primary">${clientRows || `<tr><td colspan="2" class="table-row-primary text-center text-gray-400">No data</td></tr>`}</tbody>
          </table>
        </div>
      </div>
    `
  }

  renderLeaderboard(entry) {
    const rows = (entry.leaderboard || []).map((r, i) => `
      <tr class="odd:bg-white even:bg-gray-50">
        <td class="table-row-primary text-right text-gray-400">${i + 1}</td>
        <td class="table-row-primary text-start">${r.rep}</td>
        <td class="table-row-primary text-right">${r.cut_ins}</td>
      </tr>
    `).join("")

    return `
      <div class="overflow-x-auto mb-4">
        <h3 class="text-sm font-semibold text-gray-700 mb-2">Rep Leaderboard (Top 30)</h3>
        <table class="table-primary">
          <thead><tr><th class="table-header-primary">#</th><th class="table-header-primary">REP</th><th class="table-header-primary text-right">CUT-INS</th></tr></thead>
          <tbody class="table-body-primary">${rows || `<tr><td colspan="3" class="table-row-primary text-center text-gray-400">No data</td></tr>`}</tbody>
        </table>
        <p class="text-xs text-gray-400 mt-1">Field total: ${entry.leaderboard_field_total ?? "–"} · Top 30 total: ${entry.leaderboard_top30_total ?? "–"} · Remaining: ${entry.leaderboard_remaining ?? "–"}</p>
      </div>
    `
  }

  renderDisplayTables(entry) {
    const bannerRows = (entry.displays_by_banner || []).map(r => `
      <tr class="odd:bg-white even:bg-gray-50">
        <td class="table-row-primary text-start">${r.banner}</td>
        <td class="table-row-primary text-right">${r.count}</td>
      </tr>
    `).join("")

    const clientRows = (entry.displays_by_client || []).map(r => `
      <tr class="odd:bg-white even:bg-gray-50">
        <td class="table-row-primary text-start">${r.client}</td>
        <td class="table-row-primary text-right">${r.count}</td>
      </tr>
    `).join("")

    return `
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div class="overflow-x-auto">
          <h3 class="text-sm font-semibold text-gray-700 mb-2">Display Builds by Banner</h3>
          <table class="table-primary">
            <thead><tr><th class="table-header-primary">BANNER</th><th class="table-header-primary text-right">BUILDS</th></tr></thead>
            <tbody class="table-body-primary">${bannerRows || `<tr><td colspan="2" class="table-row-primary text-center text-gray-400">No data</td></tr>`}</tbody>
          </table>
        </div>
        <div class="overflow-x-auto">
          <h3 class="text-sm font-semibold text-gray-700 mb-2">Display Builds by Client</h3>
          <table class="table-primary">
            <thead><tr><th class="table-header-primary">CLIENT</th><th class="table-header-primary text-right">BUILDS</th></tr></thead>
            <tbody class="table-body-primary">${clientRows || `<tr><td colspan="2" class="table-row-primary text-center text-gray-400">No data</td></tr>`}</tbody>
          </table>
        </div>
      </div>
    `
  }

  // ---------- Layout Results ----------

  renderLayoutSection(entry) {
    const layouts = entry.layouts || { tracked: false, message: "No layout data available." }

    if (!layouts.tracked) {
      return `
        <div class="mt-2">
          <h3 class="text-sm font-semibold text-gray-700 mb-2">Layout Results</h3>
          <div class="ics-placeholder-card">${layouts.message}</div>
        </div>
      `
    }

    const rows = layouts.score_rows || []
    const subView = this.state.layoutSubView

    return `
      <div class="mt-2">
        <h3 class="text-sm font-semibold text-gray-700 mb-2">Layout Results</h3>
        <div class="flex gap-4 border-b border-gray-200 mb-3">
          <div class="ics-view-tab ${subView === "summary" ? "active" : ""}" data-sub-view="summary">Summary</div>
          <div class="ics-view-tab ${subView === "store" ? "active" : ""}" data-sub-view="store">By Store</div>
          <div class="ics-view-tab ${subView === "rep" ? "active" : ""}" data-sub-view="rep">By Rep</div>
        </div>
        ${subView === "summary" ? this.renderLayoutSummary(rows) : ""}
        ${subView === "store" ? this.renderLayoutByStore(rows) : ""}
        ${subView === "rep" ? this.renderLayoutByRep(rows) : ""}
      </div>
    `
  }

  bindLayoutSubViewTabs() {
    this.contentTarget.querySelectorAll("[data-sub-view]").forEach(tab => {
      tab.addEventListener("click", (event) => this.selectLayoutSubView(event))
    })
  }

  scoreBadge(score) {
    if (score >= 75) return `<span class="ics-badge ics-badge-great">Great</span>`
    if (score >= 50) return `<span class="ics-badge ics-badge-good">Good</span>`
    return `<span class="ics-badge ics-badge-none">–</span>`
  }

  territoryPill(territory) {
    const cls = territory === "Tess" ? "ics-pill-tess" : "ics-pill-scott"
    return `<span class="ics-pill ${cls}">${territory}</span>`
  }

  renderLayoutSummary(rows) {
    // Grouped by client, then category within client -- one column per
    // active state (fixed order, only states present in the data).
    const STATE_ORDER = ["NSW/ACT", "QLD", "SA", "VIC", "WA"]
    const statesPresent = STATE_ORDER.filter(state => rows.some(r => r.state === state))

    const byClient = new Map()
    rows.forEach(r => {
      if (!byClient.has(r.client)) byClient.set(r.client, new Map())
      const categories = byClient.get(r.client)
      const key = r.category
      if (!categories.has(key)) categories.set(key, [])
      categories.get(key).push(r)
    })

    // Colour alone must never be the only signal (accessibility) -- the
    // title tooltip and sr-only text expose the great/good/room breakdown
    // that badge colour otherwise conveys visually alone.
    const cellHtml = (categoryRows, state) => {
      const inState = categoryRows.filter(r => r.state === state)
      if (!inState.length) return `<span class="ics-badge ics-badge-none">–</span>`
      const great = inState.filter(r => r.score >= 75).length
      const good = inState.filter(r => r.score >= 50 && r.score < 75).length
      const room = inState.length - great - good
      const cls = room === 0 && good === 0 ? "ics-badge-great" : room === 0 && great === 0 ? "ics-badge-good" : "ics-badge-mixed"
      const breakdown = `${great} great, ${good} good, ${room} room for improvement`
      return `<span class="ics-badge ${cls}" title="${breakdown}">${inState.length}<span class="sr-only"> (${breakdown})</span></span>`
    }

    let totalGood = 0
    let totalGreat = 0
    let bodyHtml = ""

    for (const [client, categories] of byClient) {
      let clientRows = ""
      for (const [category, categoryRows] of categories) {
        totalGood += categoryRows.filter(r => r.score >= 50 && r.score < 75).length
        totalGreat += categoryRows.filter(r => r.score >= 75).length
        clientRows += `
          <tr class="odd:bg-white even:bg-gray-50">
            <td class="table-row-primary text-start pl-6">↳ ${category}</td>
            ${statesPresent.map(state => `<td class="table-row-primary text-center">${cellHtml(categoryRows, state)}</td>`).join("")}
            <td class="table-row-primary text-right">${categoryRows.length}</td>
            <td class="table-row-primary text-right">${categoryRows.filter(r => r.score >= 50 && r.score < 75).length}</td>
            <td class="table-row-primary text-right">${categoryRows.filter(r => r.score >= 75).length}</td>
          </tr>
        `
      }
      bodyHtml += `
        <tr class="bg-gray-100 font-semibold">
          <td class="table-row-primary text-start" colspan="${statesPresent.length + 4}">${client}</td>
        </tr>
        ${clientRows}
      `
    }

    return `
      <div class="overflow-x-auto">
        <table class="table-primary">
          <thead>
            <tr>
              <th class="table-header-primary">CLIENT / CATEGORY</th>
              ${statesPresent.map(state => `<th class="table-header-primary text-center">${state}</th>`).join("")}
              <th class="table-header-primary text-right">TOTAL</th>
              <th class="table-header-primary text-right">GOOD</th>
              <th class="table-header-primary text-right">GREAT</th>
            </tr>
          </thead>
          <tbody class="table-body-primary">
            ${bodyHtml || `<tr><td colspan="${statesPresent.length + 4}" class="table-row-primary text-center text-gray-400">No layout data</td></tr>`}
          </tbody>
          <tfoot>
            <tr class="font-bold border-t-2 border-gray-300">
              <td class="table-row-primary text-start" colspan="${statesPresent.length + 1}">Total</td>
              <td class="table-row-primary text-right">${totalGood}</td>
              <td class="table-row-primary text-right">${totalGreat}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    `
  }

  storeSortValue(row, key) {
    if (key === "rank") return row.rank === null || row.rank === undefined ? Infinity : row.rank
    if (key === "store") return row.store.toLowerCase()
    if (key === "state") return (row.state || "").toLowerCase()
    if (key === "client") return row.client.toLowerCase()
    if (key === "rep") return row.rep.toLowerCase()
    if (key === "score") return row.score
    return row.client.toLowerCase()
  }

  renderLayoutByStore(rows) {
    const key = this.state.sortKey.store
    const dir = this.state.sortDir.store === "asc" ? 1 : -1
    const sorted = [...rows].sort((a, b) => {
      const va = this.storeSortValue(a, key)
      const vb = this.storeSortValue(b, key)
      if (va < vb) return -1 * dir
      if (va > vb) return 1 * dir
      return a.client.toLowerCase() < b.client.toLowerCase() ? -1 : 1
    })

    const arrow = (k) => this.state.sortKey.store === k
      ? `<span class="ics-sort-arrow">${this.state.sortDir.store === "asc" ? "▲" : "▼"}</span>` : ""

    const bodyRows = sorted.map(r => `
      <tr class="odd:bg-white even:bg-gray-50">
        <td class="table-row-primary text-right">${r.rank !== null && r.rank !== undefined ? "#" + r.rank : "–"}</td>
        <td class="table-row-primary text-start">${r.store}</td>
        <td class="table-row-primary">${r.state ? `<span class="ics-pill">${r.state}</span>` : ""}</td>
        <td class="table-row-primary">${this.territoryPill(r.territory)}</td>
        <td class="table-row-primary text-start">${r.client}</td>
        <td class="table-row-primary"><span class="ics-pill">${r.category}</span></td>
        <td class="table-row-primary text-start">${r.rep}</td>
        <td class="table-row-primary text-center">${this.scoreBadge(r.score)}</td>
      </tr>
    `).join("")

    const good = rows.filter(r => r.score >= 50 && r.score < 75).length
    const great = rows.filter(r => r.score >= 75).length

    return `
      <div class="overflow-x-auto">
        <table class="table-primary">
          <thead>
            <tr>
              <th class="table-header-primary ics-sortable" data-sort-view="store" data-sort-key="rank">RANK${arrow("rank")}</th>
              <th class="table-header-primary ics-sortable" data-sort-view="store" data-sort-key="store">STORE${arrow("store")}</th>
              <th class="table-header-primary ics-sortable" data-sort-view="store" data-sort-key="state">STATE${arrow("state")}</th>
              <th class="table-header-primary">TERRITORY</th>
              <th class="table-header-primary ics-sortable" data-sort-view="store" data-sort-key="client">CLIENT${arrow("client")}</th>
              <th class="table-header-primary">CATEGORY</th>
              <th class="table-header-primary ics-sortable" data-sort-view="store" data-sort-key="rep">REP${arrow("rep")}</th>
              <th class="table-header-primary ics-sortable text-center" data-sort-view="store" data-sort-key="score">SCORE${arrow("score")}</th>
            </tr>
          </thead>
          <tbody class="table-body-primary">${bodyRows || `<tr><td colspan="8" class="table-row-primary text-center text-gray-400">No layout data</td></tr>`}</tbody>
          <tfoot>
            <tr class="font-bold border-t-2 border-gray-300">
              <td class="table-row-primary text-start" colspan="6">Good: ${good} · Great: ${great}</td>
              <td colspan="2"></td>
            </tr>
          </tfoot>
        </table>
      </div>
    `
  }

  renderLayoutByRep(rows) {
    const byRep = new Map()
    rows.forEach(r => {
      if (!byRep.has(r.rep_id)) byRep.set(r.rep_id, { rep: r.rep, territory: r.territory, rows: [], stores: new Set(), clients: new Set() })
      const bucket = byRep.get(r.rep_id)
      bucket.rows.push(r)
      bucket.stores.add(r.place_id)
      bucket.clients.add(r.client)
    })

    const reps = [...byRep.values()].map(bucket => ({
      rep: bucket.rep,
      territory: bucket.territory,
      layouts: bucket.rows.length,
      stores: bucket.stores.size,
      good: bucket.rows.filter(r => r.score >= 50 && r.score < 75).length,
      great: bucket.rows.filter(r => r.score >= 75).length,
      clients: [...bucket.clients].sort().join(", ")
    }))

    const key = this.state.sortKey.rep
    const dir = this.state.sortDir.rep === "asc" ? 1 : -1
    const sortValue = (rep, k) => (k === "rep" ? rep.rep.toLowerCase() : rep[k])
    reps.sort((a, b) => {
      const va = sortValue(a, key)
      const vb = sortValue(b, key)
      if (va < vb) return -1 * dir
      if (va > vb) return 1 * dir
      return a.rep.toLowerCase() < b.rep.toLowerCase() ? -1 : 1
    })

    const arrow = (k) => this.state.sortKey.rep === k
      ? `<span class="ics-sort-arrow">${this.state.sortDir.rep === "asc" ? "▲" : "▼"}</span>` : ""

    const bodyRows = reps.map(r => `
      <tr class="odd:bg-white even:bg-gray-50">
        <td class="table-row-primary text-start">${r.rep}</td>
        <td class="table-row-primary">${this.territoryPill(r.territory)}</td>
        <td class="table-row-primary text-right">${r.layouts}</td>
        <td class="table-row-primary text-right">${r.stores}</td>
        <td class="table-row-primary text-right">${this.scoreBadge(50)} ${r.good}</td>
        <td class="table-row-primary text-right">${this.scoreBadge(75)} ${r.great}</td>
        <td class="table-row-primary text-start">${r.clients}</td>
      </tr>
    `).join("")

    const totalLayouts = reps.reduce((sum, r) => sum + r.layouts, 0)
    const totalGood = reps.reduce((sum, r) => sum + r.good, 0)
    const totalGreat = reps.reduce((sum, r) => sum + r.great, 0)

    return `
      <div class="overflow-x-auto">
        <table class="table-primary">
          <thead>
            <tr>
              <th class="table-header-primary ics-sortable" data-sort-view="rep" data-sort-key="rep">REP${arrow("rep")}</th>
              <th class="table-header-primary">TERRITORY</th>
              <th class="table-header-primary ics-sortable text-right" data-sort-view="rep" data-sort-key="layouts">LAYOUTS${arrow("layouts")}</th>
              <th class="table-header-primary ics-sortable text-right" data-sort-view="rep" data-sort-key="stores">STORES${arrow("stores")}</th>
              <th class="table-header-primary ics-sortable text-right" data-sort-view="rep" data-sort-key="good">GOOD${arrow("good")}</th>
              <th class="table-header-primary ics-sortable text-right" data-sort-view="rep" data-sort-key="great">GREAT${arrow("great")}</th>
              <th class="table-header-primary">CLIENTS COVERED</th>
            </tr>
          </thead>
          <tbody class="table-body-primary">${bodyRows || `<tr><td colspan="7" class="table-row-primary text-center text-gray-400">No layout data</td></tr>`}</tbody>
          <tfoot>
            <tr class="font-bold border-t-2 border-gray-300">
              <td class="table-row-primary text-start" colspan="2">Total</td>
              <td class="table-row-primary text-right">${totalLayouts}</td>
              <td></td>
              <td class="table-row-primary text-right">${totalGood}</td>
              <td class="table-row-primary text-right">${totalGreat}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
    `
  }

  bindSortableHeaders() {
    this.contentTarget.querySelectorAll("[data-sort-key]").forEach(th => {
      th.addEventListener("click", (event) => this.setSort(event))
    })
  }
}
